import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { useAuth } from "@/context/AuthContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const STAGES = ["applied", "screening", "shortlisted", "interview", "offer", "hired", "rejected", "withdrawn"];

export default function Employer() {
  const { user } = useAuth();
  return <EmployerPortal key={JSON.stringify([user?.id, user?.tenant_id])} />;
}

function EmployerPortal() {
  const [emp, setEmp] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [msg, setMsg] = useState("");
  const [company, setCompany] = useState({ company_name: "", industry: "Health and Social Care", town: "", holds_sponsor_licence: false });
  const [job, setJob] = useState({ title: "", description: "", town: "", industry: "Health and Social Care", salary_min: 28000, salary_max: 35000, occupation_code: "", skills: "", sponsorship_available: false, sponsorship_wording: "" });

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [messageError, setMessageError] = useState(false);
  const [pending, setPending] = useState("");
  const actionPending = useRef(false);
  const loadVersion = useRef(0);
  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true); setLoadError("");
    try {
      const [j, a] = await Promise.all([api.get("/employer/jobs"), api.get("/employer/applications")]);
      if (version !== loadVersion.current) return;
      setJobs(j.data.items || []); setEmp(j.data.employer || null);
      setApps(a.data.items || []);
    } catch (error) {
      if (version === loadVersion.current) setLoadError(applicationErrorMessage(error, "Unable to load your employer portal. Please try again."));
    } finally { if (version === loadVersion.current) setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAction = async (key, action, fallback) => {
    if (actionPending.current) return;
    actionPending.current = true; setPending(key); setMsg(""); setMessageError(false);
    try { await action(); } catch (error) {
      setMessageError(true); setMsg(applicationErrorMessage(error, fallback));
    } finally { actionPending.current = false; setPending(""); }
  };
  const invalid = (message) => { setMessageError(true); setMsg(message); };
  const createCompany = async () => {
    if (actionPending.current) return;
    if (!company.company_name.trim() || !company.town.trim()) {
      invalid("Enter your company name and town."); return;
    }
    await runAction("company", async () => {
      const { data } = await api.post("/employers", company);
      setEmp(data);
      setMsg("Company profile created and submitted for verification.");
    }, "Unable to create your company profile. Please try again.");
  };

  const createJob = async () => {
    if (actionPending.current) return;
    const minimum = Number(job.salary_min), maximum = Number(job.salary_max);
    if (!job.title.trim() || !job.description.trim()) {
      invalid("Enter a vacancy title and description."); return;
    }
    if (job.salary_min === "" || job.salary_max === "" || !Number.isInteger(minimum) || !Number.isInteger(maximum) || minimum < 0 || maximum < minimum) {
      invalid("Enter whole-number salaries of zero or more, with the maximum at least the minimum."); return;
    }
    await runAction("job", async () => {
      const { data } = await api.post("/jobs", { ...job, skills: job.skills.split(",").map((s) => s.trim()).filter(Boolean), salary_min: minimum, salary_max: maximum });
      setJobs((items) => [data, ...items]);
      setJob((current) => ({ ...current, title: "", description: "", skills: "" }));
      setMsg("Vacancy created. It is awaiting administrator approval before publication.");
    }, "Unable to create vacancy. Please try again.");
  };

  const move = async (id, stage) => {
    await runAction("stage:" + id, async () => {
      const { data } = await api.put(`/employer/applications/${id}/stage`, { stage });
      setApps((items) => items.map((item) => item.id === id ? { ...item, stage } : item));
      setMsg(data?.warning || "Application stage updated.");
      setMessageError(!!data?.warning);
    }, "Unable to update this application stage. Please try again.");
  };

  if (loading || loadError) return <div className="max-w-7xl mx-auto px-5 py-10" data-testid="employer-page">
    <h1 className="text-3xl font-semibold">Employer portal</h1>
    {loading ? <p role="status" className="mt-4 text-sm">Loading employer records…</p> : <>
      <p role="alert" className="mt-4 text-sm text-red-700">{loadError}</p>
      <button className="btn btn-primary mt-4" onClick={load}>Retry loading</button>
    </>}
  </div>;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="employer-page">
      <p className="overline">Employer portal</p>
      <h1 className="mt-3 text-3xl font-semibold">{emp ? emp.company_name : "Register your company"}</h1>
      <button className="btn btn-ghost mt-3" disabled={!!pending} onClick={load}>Refresh records</button>
      {msg && <p role={messageError ? "alert" : "status"} data-testid="employer-message" className={messageError ? "mt-3 text-sm text-red-700" : "mt-3 text-sm text-emerald-700"}>{msg}</p>}

      {!emp ? (
        <fieldset disabled={!!pending} className="card p-6 mt-6 max-w-xl space-y-3" data-testid="company-form">
          {["company_name", "town"].map((f) => (
            <label key={f} className="block text-sm capitalize">{f.replace(/_/g, " ")}
              <input data-testid={`company-${f}-input`} className="field mt-1" value={company[f]}
                onChange={(e) => setCompany({ ...company, [f]: e.target.value })} />
            </label>
          ))}
          <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={company.holds_sponsor_licence} onChange={(event) => setCompany({ ...company, holds_sponsor_licence: event.target.checked })} />Our organisation holds a sponsor licence</label>
          <button data-testid="create-company-button" onClick={createCompany} className="btn btn-primary w-full justify-center">{pending === "company" ? "Creating company…" : "Create company profile"}</button>
        </fieldset>
      ) : (
        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <section className="card p-6" data-testid="create-job-form">
            <h2 className="text-lg font-semibold">Create a vacancy</h2>
            <fieldset disabled={!!pending}>
            <label className="block text-sm mt-3">Job title<input data-testid="job-title-input" className="field mt-1" value={job.title} onChange={(e) => setJob({ ...job, title: e.target.value })} /></label>
            <label className="block text-sm mt-3">Description<textarea data-testid="job-description-input" rows={3} className="field mt-1" value={job.description} onChange={(e) => setJob({ ...job, description: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="block text-sm">Town<input data-testid="job-town-field" className="field mt-1" value={job.town} onChange={(e) => setJob({ ...job, town: e.target.value })} /></label>
              <label className="block text-sm">Occupation code<input data-testid="job-occupation-input" className="field mt-1" value={job.occupation_code} onChange={(e) => setJob({ ...job, occupation_code: e.target.value })} /></label>
              <label className="block text-sm">Salary from<input data-testid="job-salary-min-input" type="number" className="field mt-1" value={job.salary_min} onChange={(e) => setJob({ ...job, salary_min: e.target.value })} /></label>
              <label className="block text-sm">Salary to<input data-testid="job-salary-max-input" type="number" className="field mt-1" value={job.salary_max} onChange={(e) => setJob({ ...job, salary_max: e.target.value })} /></label>
            </div>
            <label className="block text-sm mt-3">Skills (comma separated)<input data-testid="job-skills-input" className="field mt-1" value={job.skills} onChange={(e) => setJob({ ...job, skills: e.target.value })} /></label>
            <label className="flex items-center gap-2 text-sm mt-3">
              <input data-testid="job-sponsorship-checkbox" type="checkbox" checked={job.sponsorship_available}
                onChange={(e) => setJob({ ...job, sponsorship_available: e.target.checked })} />
              Sponsorship available for eligible candidates
            </label>
            <label className="block text-sm mt-3">Supporting sponsorship wording<textarea className="field mt-1" value={job.sponsorship_wording} onChange={(event) => setJob({ ...job, sponsorship_wording: event.target.value })} /></label>
            <button data-testid="create-job-button" onClick={createJob} className="btn btn-primary mt-4 w-full justify-center">{pending === "job" ? "Submitting vacancy…" : "Submit vacancy for approval"}</button>
            </fieldset>
          </section>

          <section className="card p-6" data-testid="employer-jobs-panel">
            <h2 className="text-lg font-semibold">Your vacancies ({jobs.length})</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {jobs.map((j) => <li key={j.id} className="flex justify-between"><span>{j.title}</span><span className="pill bg-slate-100 text-slate-700">{j.status}</span></li>)}
            </ul>
          </section>

          <section className="card p-6 lg:col-span-2" data-testid="pipeline-panel">
            <h2 className="text-lg font-semibold">Applicant pipeline ({apps.length})</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500 text-xs uppercase tracking-wider">
                  <th className="py-2">Candidate</th><th>Vacancy</th><th>Stage</th><th>Sponsorship compatibility</th><th>Move to</th><th>Communication</th>
                </tr></thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id} className="border-t border-slate-200" data-testid={`application-row-${a.id}`}>
                      <td className="py-2">{a.candidate_name}</td>
                      <td>{a.job_title}</td>
                      <td><span className="pill bg-blue-50 text-blue-800">{a.stage}</span></td>
                      <td className="text-xs text-slate-600">{a.match?.dimensions?.sponsorship_compatibility?.score}%</td>
                      <td>
                        <select data-testid={`stage-select-${a.id}`} className="field w-auto" disabled={!!pending} value={a.stage}
                          onChange={(e) => move(a.id, e.target.value)}>
                          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {pending === "stage:" + a.id && <span role="status" className="ml-2 text-xs">Updating…</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-500">Match dimensions are advisory. Employers make all final hiring decisions.</p>
          </section>
        </div>
      )}
    </div>
  );
}
